import type { DataApiInputParams } from 'src/types/api';

import type { Builder } from './utils';
import { appendFormData, getHeaders, getQueryString } from './utils';

const PROJECTS_ENDPOINTS = {
  base: 'projects',
  accessRights: 'projects/project-perms-details'
} as const;

export default (builder: Builder) => {
  return {
    getExistingProjects: builder.query({
      async queryFn(arg: DataApiInputParams, _, extraOptions, fetchWithBQ) {
        const url = `${PROJECTS_ENDPOINTS.base}?${getQueryString(arg.params)}`;
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      },
      providesTags: ['Projects']
    }),
    createProject: builder.mutation({
      async queryFn(args: { params: any }, _, extraOptions, baseQuery) {
        const formData = appendFormData(args.params);

        const result: any = await baseQuery({
          url: PROJECTS_ENDPOINTS.base,
          method: 'POST',
          body: formData,
          ...getHeaders(PROJECTS_ENDPOINTS.base, formData)
        });
        if (result.error) {
          return result;
        }
        return { data: result ?? { msg: 'Project created successfully' } };
      },
      invalidatesTags: ['Projects']
    }),
    updateProject: builder.mutation({
      async queryFn(args: { params: any }, _, extraOptions, baseQuery) {
        const formData = appendFormData(args.params);

        const result: any = await baseQuery({
          url: PROJECTS_ENDPOINTS.base,
          method: 'PATCH',
          body: formData,
          ...getHeaders(PROJECTS_ENDPOINTS.base, formData)
        });
        if (result.error) {
          return result;
        }
        return { data: result ?? { msg: 'Project updated successfully' } };
      },
      invalidatesTags: ['Projects']
    }),
    deleteProject: builder.mutation({
      async queryFn(
        args: { params: { id: number } },
        _,
        extraOptions,
        baseQuery
      ) {
        const formData = appendFormData(args.params);

        const result: any = await baseQuery({
          url: PROJECTS_ENDPOINTS.base,
          method: 'DELETE',
          body: formData,
          ...getHeaders(PROJECTS_ENDPOINTS.base, formData)
        });
        if (result.error) {
          return result;
        }
        return { data: result ?? { msg: 'Project deleted successfully' } };
      },
      invalidatesTags: ['Projects']
    }),

    getProjectAccessRights: builder.query({
      async queryFn(arg: DataApiInputParams, _, extraOptions, fetchWithBQ) {
        const url = `${PROJECTS_ENDPOINTS.accessRights}?${getQueryString(
          arg.params
        )}`;
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      },
      providesTags: ['Projects']
    }),
    updateProjectResource: builder.mutation({
      async queryFn({ params }, _, extraOptions, baseQuery) {
        const formData = appendFormData(params);
        return baseQuery({
          url: PROJECTS_ENDPOINTS.accessRights,
          method: 'POST',
          body: formData,
          ...getHeaders(PROJECTS_ENDPOINTS.accessRights, formData)
        });
      },
      invalidatesTags: ['Projects']
    })
  };
};
